/** @format */

import { DAY_MIN, PAD, SNAP } from './constants';
import { m2t } from './time';

/** How far before the week start a drag may step (2 hours). */
export const EARLY_DRAG_LEAD_MIN = 2 * 60;

/** Top strip of the day cell that scrolls the window earlier. */
export const EARLY_DRAG_EDGE_PX = 14;

/** Class of the translated layer inside a day cell. */
export const DAY_CELL_SCROLL_CLASS = 'dc-scroll';

/** Delay between 15-minute scrolls while the pointer stays on an edge. */
export const EARLY_DRAG_SCROLL_MS = 50;

const EARLY_DRAG_TONE = '#185FA5';
const DEFAULT_DRAG_TONE = '#00B37E';

export function earlyDragFloorMin(dayStartMin) {
  const start = Number(dayStartMin);
  if (!Number.isFinite(start)) return 0;
  return Math.max(0, start - EARLY_DRAG_LEAD_MIN);
}

export function earlyDragMaxOffset(dayStartMin) {
  const start = Number(dayStartMin);
  if (!Number.isFinite(start)) return 0;
  return Math.max(0, start - earlyDragFloorMin(start));
}

export function isPointerInEarlyDragZone(rect, clientY) {
  if (!rect || !Number.isFinite(clientY)) return false;
  return clientY <= rect.top + EARLY_DRAG_EDGE_PX;
}

/** Bottom strip scrolls the window back toward the week start. */
export function isPointerInEarlyDragReturnZone(rect, clientY) {
  if (!rect || !Number.isFinite(clientY)) return false;
  return clientY >= rect.bottom - EARLY_DRAG_EDGE_PX;
}

export function earlyDragStartMin(dayStartMin, offsetMin) {
  const floor = earlyDragFloorMin(dayStartMin);
  const offset = Math.max(0, Number(offsetMin) || 0);
  if (!Number.isFinite(dayStartMin)) return floor;
  return Math.max(floor, dayStartMin - offset);
}

export function nextEarlyDragOffset(dayStartMin, offsetMin) {
  const maxOffset = earlyDragMaxOffset(dayStartMin);
  const current = Math.max(0, Number(offsetMin) || 0);
  if (current >= maxOffset) return maxOffset;
  return Math.min(maxOffset, current + SNAP);
}

export function earlyDragSnapLabel(startMin, endMin, { holding = false } = {}) {
  const range = `${m2t(startMin)} → ${m2t(endMin)}`;
  if (!holding) return range;
  return `${range}`;
}

export function dayCellScrollPx(scrollMin, innerHeight) {
  const inner = Number(innerHeight);
  const usable = Number.isFinite(inner) && inner > 0 ? inner : 1;
  const offset = Math.max(0, Number(scrollMin) || 0);
  return (offset / DAY_MIN) * usable;
}

export function applyDayCellEarlyScroll(cell, scrollMin) {
  const scroller = cell?.querySelector?.(`.${DAY_CELL_SCROLL_CLASS}`);
  if (!scroller) return;
  const inner = Number(cell.dataset?.inner);
  const usable =
    Number.isFinite(inner) && inner > 0
      ? inner
      : cell.getBoundingClientRect().height - PAD * 2;
  const px = dayCellScrollPx(scrollMin, usable);
  scroller.style.transform = px > 0 ? `translateY(${px}px)` : '';
}

export function clearDayCellEarlyScroll(cell) {
  applyDayCellEarlyScroll(cell, 0);
}

export function earlyDragHighlightOptions(
  startMin,
  durationMin,
  { holding = false, early = false } = {},
) {
  const endMin = startMin + durationMin;
  return {
    snapText: earlyDragSnapLabel(startMin, endMin, { holding }),
    snapTone: holding || early ? 'early' : 'default',
  };
}

/**
 * Scroll the day window while the pointer stays on the top or bottom edge.
 * The offset stays put once the pointer leaves the edge, so the drop is the
 * time under the pointer, not a tick you have to catch.
 */
export function createEarlyDragScroll({
  stepMs = EARLY_DRAG_SCROLL_MS,
  onScroll,
} = {}) {
  let scrollMin = 0;
  let timer = null;
  let key = null;
  let dayStart = null;
  let cell = null;
  let edge = null;

  const snapshot = () => ({
    scrollMin,
    originMin: Number.isFinite(dayStart)
      ? earlyDragStartMin(dayStart, scrollMin)
      : null,
    edge,
    early: scrollMin > 0,
    scrolling: edge === 'top' || edge === 'bottom',
  });

  const stop = () => {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  };

  const canStep = (direction) => {
    if (!Number.isFinite(dayStart) || !direction) return false;
    const delta = direction === 'top' ? SNAP : -SNAP;
    const next = Math.max(
      0,
      Math.min(earlyDragMaxOffset(dayStart), scrollMin + delta),
    );
    return next !== scrollMin;
  };

  const tick = () => {
    if (!canStep(edge)) {
      stop();
      return;
    }
    const delta = edge === 'top' ? SNAP : -SNAP;
    scrollMin = Math.max(
      0,
      Math.min(earlyDragMaxOffset(dayStart), scrollMin + delta),
    );
    applyDayCellEarlyScroll(cell, scrollMin);
    if (onScroll) onScroll(snapshot());
  };

  const arm = () => {
    if (timer || !canStep(edge)) return;
    timer = setInterval(tick, stepMs);
  };

  return {
    reset() {
      clearDayCellEarlyScroll(cell);
      scrollMin = 0;
      key = null;
      dayStart = null;
      cell = null;
      edge = null;
      stop();
    },
    sync({
      edge: nextEdge = null,
      cellKey = null,
      dayStartMin = null,
      cellEl = null,
      seedScroll = 0,
    } = {}) {
      if (!Number.isFinite(dayStartMin)) {
        this.reset();
        return snapshot();
      }
      const switched = cellKey !== key || dayStartMin !== dayStart;
      if (switched) {
        clearDayCellEarlyScroll(cell);
        const maxOffset = earlyDragMaxOffset(dayStartMin);
        scrollMin = Math.max(0, Math.min(maxOffset, Number(seedScroll) || 0));
        key = cellKey;
        dayStart = dayStartMin;
        cell = cellEl;
        stop();
      } else if (cellEl) {
        cell = cellEl;
      }
      const normalized =
        nextEdge === 'top' || nextEdge === 'bottom' ? nextEdge : null;
      if (normalized !== edge) {
        edge = normalized;
        stop();
      }
      arm();
      applyDayCellEarlyScroll(cell, scrollMin);
      return snapshot();
    },
  };
}

export function earlyDragToneColor(snapTone) {
  return snapTone === 'early' ? EARLY_DRAG_TONE : DEFAULT_DRAG_TONE;
}
